import { Controller } from "@hotwired/stimulus"
import { confirmAction } from "confirmation_modal"

// Draft text stays local; private staged photos never bypass the server's publication checks.
export default class extends Controller {
  static targets = ["editor", "preview", "summary", "status", "input", "photos", "persistedPhotos", "newPhotoTemplate", "photoError", "photoStatus", "uploadFields", "verb", "photoHelp", "priceLabel", "priceHelp", "contactHelp", "pickupLocation", "quantityField"]
  static values = { draft: Boolean, resume: Boolean, key: String, login: String, upload: String, errors: Object }

  async connect() {
    this.files = []
    this.fileIds = []
    this.uploads = []
    this.removedFileIds = new Set()
    this.nextFileId = 0
    this.urls = []
    this.ready = false
    this.hydrated = false
    this.hadStoredDraft = false
    this.element.noValidate = true
    if (this.draftValue) {
      try {
        this.db = await new Promise((resolve, reject) => {
          const request = indexedDB.open("makerbazar-drafts", 1)
          request.onupgradeneeded = () => request.result.createObjectStore("drafts")
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })
        const guest = this.resumeValue && this.keyValue !== "guest" ? await this.storage("get", "guest") : null
        const draft = guest || await this.storage("get", this.keyValue)
        if (draft) {
          this.hadStoredDraft = true
          for (const [id, value] of Object.entries(draft.fields)) {
            const field = this.element.querySelector(`#${CSS.escape(id)}`)
            if (!field) continue
            if (["checkbox", "radio"].includes(field.type)) field.checked = value
            else field.value = value
          }
          this.files = draft.files
          this.uploads = Array.isArray(draft.uploads) ? draft.uploads : []
          this.currencyChosen = draft.currencyChosen
          this.statusTarget.textContent = "Obnovený koncept je v tomto prohlížeči. Fotografie jsou soukromě nahrané ke kontrole."
          if (guest) {
            await this.storage("put", this.keyValue, guest)
            await this.storage("delete", "guest")
          }
        }
      } catch {
        this.storageFailed()
      }
    }
    this.fileIds = this.files.map(() => this.newFileId())
    this.uploads = this.files.map((_, index) => this.uploads[index] || null)
    this.hydrated = true
    this.syncPhotos()
    await this.validateRestoredUploads()
    if (this.hadStoredDraft) await this.save()
    this.refreshPersistedPhotos()
    this.updateContext()
    this.ready = true
    this.showServerErrors()
  }

  disconnect() {
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.db?.close()
  }

  storage(method, key, value) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction("drafts", method === "get" ? "readonly" : "readwrite")
      const store = transaction.objectStore("drafts")
      const request = method === "put" ? store.put(value, key) : store[method](key)
      transaction.oncomplete = () => resolve(request.result)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  storageFailed() {
    this.statusTarget.textContent = "Koncept se nepodařilo uložit. Nezavírejte stránku; zkontrolujte volné místo a povolení úložiště prohlížeče."
  }

  async save() {
    if (!this.draftValue || !this.hydrated) return false
    const fields = {}
    this.element.querySelectorAll("#listing_submission_id, input[id]:not([type=hidden]):not([type=file]), select[id], textarea[id]").forEach(field => {
      fields[field.id] = ["checkbox", "radio"].includes(field.type) ? field.checked : field.value
    })
    try {
      if (!this.db) throw new Error("Storage unavailable")
      await this.storage("put", this.keyValue, { fields, files: this.files, uploads: this.uploads, currencyChosen: this.currencyChosen })
      this.statusTarget.textContent = "Koncept je uložený v tomto prohlížeči. Fotografie jsou soukromě nahrané ke kontrole."
      return true
    } catch {
      this.storageFailed()
      return false
    }
  }

  field(name) { return this.element.querySelector(`#listing_${name}`) }

  changed(event) {
    if (!this.ready || event.target === this.inputTarget) return
    if (event.target.matches("input[name='listing[remove_photo_ids][]']")) this.refreshPersistedPhotos()
    if (event.target === this.field("currency")) this.currencyChosen = true
    if (event.target === this.field("country") && !this.currencyChosen) {
      this.field("currency").value = this.field("country").value === "sk" ? "eur" : "czk"
    }
    this.updateContext()
    this.updateCustomValidity()
    this.clearCorrectedValidationErrors()
    this.save()
  }

  updateContext() {
    const wanted = this.field("listing_type_wanted").checked
    const pickup = this.field("personal_handover").checked
    this.pickupLocationTargets.forEach(element => { element.hidden = !pickup })
    this.field("locality").required = pickup
    this.field("locality").disabled = !pickup
    this.field("postal_code").disabled = !pickup
    this.verbTarget.textContent = wanted ? "sháníte" : "prodáváte"
    this.photoHelpTarget.textContent = wanted ? "Fotografie je nepovinná. Pokud ji přidáte, použijte vlastní." : "Přidejte alespoň jednu vlastní fotku. První bude hlavní."
    this.priceLabelTarget.textContent = wanted ? "Rozpočet (nepovinné)" : "Cena"
    this.priceHelpTarget.textContent = wanted ? "Kolik chcete nejvýše zaplatit? Můžete nechat prázdné." : "Zadejte částku. Pokud darujete, napište 0."
    this.field("condition").required = !wanted
    this.field("condition").options[0].textContent = wanted ? "Libovolný stav (nepovinné)" : "Vyberte stav"
    this.field("price_amount").required = !wanted
    this.field("currency").required = !wanted || this.field("price_amount").value !== ""
    this.quantityFieldTarget.hidden = wanted
    this.field("quantity").disabled = wanted
    this.field("quantity").required = !wanted
    const custom = this.field("contact_email_enabled").checked || this.field("contact_phone").value || !this.field("contact_form_enabled").checked
    this.contactHelpTarget.textContent = custom ? "Použije se vámi zvolený kontakt pro přihlášené zájemce." : "Zájemci vám napíšou přes formulář. Vaši e-mailovou adresu nezveřejníme."
  }

  choosePhotos() {
    if (this.ready && !this.processingPhotos) this.inputTarget.click()
  }

  newFileId() {
    this.nextFileId += 1
    return this.nextFileId.toString()
  }

  movePersistedPhoto(event) {
    const card = event.currentTarget.closest("[data-photo-card]")
    const retained = Array.from(this.persistedPhotosTarget.querySelectorAll("[data-photo-card]"))
      .filter(candidate =>
        !candidate.querySelector("input[name='listing[remove_photo_ids][]']")?.checked && !this.removedFileIds.has(candidate.dataset.photoFileId)
      )
    const position = retained.indexOf(card)
    const sibling = retained[position + (event.params.direction === "backward" ? -1 : 1)]
    if (!sibling) return

    if (event.params.direction === "backward") sibling.before(card)
    else sibling.after(card)
    this.refreshPersistedPhotos()
  }

  toggleNewPhoto(event) {
    const id = event.currentTarget.closest("[data-photo-card]").dataset.photoFileId
    if (!this.fileIds.includes(id)) return

    if (this.removedFileIds.has(id)) this.removedFileIds.delete(id)
    else this.removedFileIds.add(id)
    this.refreshPersistedPhotos()
    this.updateCustomValidity()
  }

  togglePersistedPhoto(event) {
    const checkbox = event.currentTarget.closest("[data-photo-card]").querySelector("input[name='listing[remove_photo_ids][]']")
    checkbox.checked = !checkbox.checked
    this.refreshPersistedPhotos()
    this.updateCustomValidity()
  }

  dragPersistedPhoto(event) {
    this.draggedPhotoCard = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", event.currentTarget.querySelector("input[name='listing[photo_order][]']").value)
    event.currentTarget.classList.add("opacity-50")
  }

  dragOverPersistedPhoto(event) {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.draggedPhotoCard || target === this.draggedPhotoCard) return

    const bounds = target.getBoundingClientRect()
    const differentRow = Math.abs(event.clientY - (bounds.top + bounds.height / 2)) > bounds.height / 4
    const insertBefore = differentRow ? event.clientY < bounds.top + bounds.height / 2 : event.clientX < bounds.left + bounds.width / 2
    target[insertBefore ? "before" : "after"](this.draggedPhotoCard)
  }

  dropPersistedPhoto(event) {
    event.preventDefault()
    this.refreshPersistedPhotos()
  }

  endPersistedPhotoDrag(event) {
    event.currentTarget.classList.remove("opacity-50")
    this.draggedPhotoCard = null
    this.refreshPersistedPhotos()
  }

  refreshPersistedPhotos() {
    if (!this.hasPersistedPhotosTarget) return

    const cards = Array.from(this.persistedPhotosTarget.querySelectorAll("[data-photo-card]"))
    const retained = cards.filter(card =>
      !card.querySelector("input[name='listing[remove_photo_ids][]']")?.checked && !this.removedFileIds.has(card.dataset.photoFileId)
    )
    cards.forEach(card => {
      const removed = !retained.includes(card)
      const position = retained.indexOf(card)
      card.classList.toggle("border-action", !removed && position === 0)
      card.classList.toggle("border-warm-border", removed || position !== 0)
      card.querySelector("[data-photo-visual]").classList.toggle("grayscale", removed)
      card.querySelector("[data-photo-visual]").classList.toggle("opacity-50", removed)
      card.draggable = !removed
      const buttons = card.querySelectorAll("button[data-action='listing-form#movePersistedPhoto']")
      buttons[0].disabled = removed || position === 0
      buttons[1].disabled = removed || position === retained.length - 1
      const removeButton = card.querySelector("[data-photo-remove-button]")
      removeButton.setAttribute("aria-label", removed ? "Ponechat fotografii" : "Odebrat fotografii")
      removeButton.title = removed ? "Ponechat fotografii" : "Odebrat fotografii"
      removeButton.classList.toggle("border-red-700", removed)
      removeButton.classList.toggle("bg-red-700", removed)
      removeButton.classList.toggle("border-ink", !removed)
      removeButton.classList.toggle("bg-ink", !removed)
      const newPhotoOrder = card.dataset.photoFileId && card.querySelector("input[name='listing[photo_order][]']")
      if (newPhotoOrder) newPhotoOrder.disabled = removed
    })
    if (!this.draftValue) this.syncNewPhotoOrderFromCards()
  }

  async addPhotos() {
    if (this.processingPhotos) return
    const additions = Array.from(this.inputTarget.files)
    const existing = this.element.querySelectorAll("input[name='listing[remove_photo_ids][]']:not(:checked)").length
    const retainedNewPhotos = this.fileIds.filter(id => !this.removedFileIds.has(id)).length
    // Some mobile file pickers omit HEIC's MIME type. The server checks the actual bytes.
    const invalid = additions.some(file => (!["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/avif"].includes(file.type) && !/\.(heic|heif|avif)$/i.test(file.name)) || file.size > 10 * 1024 * 1024)
    if (invalid || existing + retainedNewPhotos + additions.length > 10) {
      this.photoErrorTarget.textContent = "Vyberte nejvýše 10 fotek, každou do 10 MB, ve formátu JPEG, PNG, WebP, HEIC/HEIF nebo AVIF. Předchozí fotky zůstaly zachované."
    } else {
      this.photoErrorTarget.textContent = ""
      this.processingPhotos = true
      this.inputTarget.disabled = true
      this.photosTarget.setAttribute("aria-busy", "true")
      try {
        const processed = []
        for (const [index, file] of additions.entries()) {
          this.photoStatusTarget.textContent = `Připravuji fotografii ${index + 1} z ${additions.length}…`
          processed.push(await this.processPhoto(file))
        }
        if (!this.element.isConnected) return
        const firstNewIndex = this.files.length
        this.files.push(...processed)
        this.fileIds.push(...processed.map(() => this.newFileId()))
        this.uploads.push(...processed.map(() => null))
        await Promise.all(processed.map((_, index) => this.uploadPhoto(firstNewIndex + index)))
      } catch (error) {
        this.photoErrorTarget.textContent = `${error.message} Předchozí fotky zůstaly zachované.`
      } finally {
        this.processingPhotos = false
        this.inputTarget.disabled = false
        this.photosTarget.removeAttribute("aria-busy")
        this.photoStatusTarget.textContent = ""
      }
    }
    this.syncPhotos()
    this.save()
  }

  async processPhoto(file) {
    let image
    try {
      image = await createImageBitmap(file, { imageOrientation: "from-image" })
    } catch {
      // HEIC and other formats unavailable in this browser are decoded and checked on the server.
      return file
    }
    try {
      if (image.width * image.height > 50_000_000 || Math.max(image.width, image.height) > 16_384) {
        throw new Error("Fotka může mít nejvýše 50 megapixelů a 16384 pixelů na straně.")
      }
      const side = Math.min(image.width, image.height)
      const canvas = document.createElement("canvas")
      canvas.width = canvas.height = Math.min(side, 1200)
      canvas.getContext("2d").drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, canvas.width, canvas.height)
      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", 0.8))
      if (!blob || blob.type !== "image/webp") return file
      return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.webp`, { type: blob.type, lastModified: file.lastModified })
    } finally {
      image.close()
    }
  }

  syncPhotos() {
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.urls = this.files.map(file => URL.createObjectURL(file))
    if (this.draftValue) this.syncDraftPhotos()
    else this.syncEditPhotos()
    this.syncPhotoInput()
    if (this.ready) {
      this.updateCustomValidity()
      this.clearCorrectedValidationErrors()
    }
  }

  syncDraftPhotos() {
    this.photosTarget.replaceChildren()
    this.files.forEach((file, index) => {
      const card = document.createElement("div")
      card.className = "rounded-sm border border-warm-border p-2 space-y-2"
      const image = document.createElement("img")
      image.src = this.urls[index]
      image.alt = `Fotografie ${index + 1}: ${file.name}`
      image.className = "aspect-square w-full object-cover"
      image.addEventListener("error", () => this.photoPreviewUnavailable(image, file.name), { once: true })
      card.append(image)
      const uploadStatus = document.createElement("p")
      uploadStatus.className = "text-xs text-muted"
      uploadStatus.dataset.photoUploadStatus = ""
      uploadStatus.textContent = this.uploadStatusText(this.uploads[index])
      card.append(uploadStatus)
      const button = (text, action) => {
        const element = document.createElement("button")
        element.type = "button"
        element.className = "block min-h-11 text-sm underline"
        element.textContent = text
        element.addEventListener("click", action)
        card.append(element)
      }
      if (index === 0) {
        const label = document.createElement("p")
        label.className = "text-sm font-semibold"
        label.textContent = this.draftValue ? "Hlavní fotografie" : "Nová fotografie"
        card.append(label)
      } else {
        button("Posunout dopředu", () => {
          [this.files[index - 1], this.files[index]] = [this.files[index], this.files[index - 1]]
          ;[this.fileIds[index - 1], this.fileIds[index]] = [this.fileIds[index], this.fileIds[index - 1]]
          ;[this.uploads[index - 1], this.uploads[index]] = [this.uploads[index], this.uploads[index - 1]]
          this.syncPhotos()
          this.save()
        })
      }
      button(`Odebrat fotku ${index + 1}`, () => {
        this.deleteUpload(this.uploads[index])
        this.files.splice(index, 1)
        this.fileIds.splice(index, 1)
        this.uploads.splice(index, 1)
        this.syncPhotos()
        this.save()
      })
      this.photosTarget.append(card)
    })
  }

  syncEditPhotos() {
    const existingOrder = Array.from(this.photosTarget.querySelectorAll("[data-photo-card]"), card => this.photoCardKey(card))
    this.photosTarget.querySelectorAll("[data-photo-file-id]").forEach(card => card.remove())

    this.files.forEach((file, index) => {
      const card = this.newPhotoTemplateTarget.content.firstElementChild.cloneNode(true)
      card.dataset.photoFileId = this.fileIds[index]
      const image = document.createElement("img")
      image.src = this.urls[index]
      image.alt = `Nová fotografie: ${file.name}`
      image.className = "aspect-square w-full object-cover"
      image.addEventListener("error", () => this.photoPreviewUnavailable(image, file.name), { once: true })
      card.querySelector("[data-photo-visual]").append(image)
      this.photosTarget.append(card)
    })

    const cards = Array.from(this.photosTarget.querySelectorAll("[data-photo-card]"))
    const cardsByKey = new Map(cards.map(card => [this.photoCardKey(card), card]))
    const order = existingOrder.filter(key => cardsByKey.has(key))
    cards.forEach(card => {
      const key = this.photoCardKey(card)
      if (!order.includes(key)) order.push(key)
    })
    order.forEach(key => this.photosTarget.append(cardsByKey.get(key)))
    this.refreshPersistedPhotos()
  }

  photoCardKey(card) {
    if (card.dataset.photoFileId) return `new:${card.dataset.photoFileId}`
    return `saved:${card.querySelector("input[name='listing[photo_order][]']").value}`
  }

  syncNewPhotoOrderFromCards() {
    const orderedIds = Array.from(this.photosTarget.querySelectorAll("[data-photo-file-id]"), card => card.dataset.photoFileId)
    const filesById = new Map(this.fileIds.map((id, index) => [id, { file: this.files[index], url: this.urls[index], upload: this.uploads[index] }]))
    this.fileIds = orderedIds
    this.files = orderedIds.map(id => filesById.get(id).file)
    this.urls = orderedIds.map(id => filesById.get(id).url)
    this.uploads = orderedIds.map(id => filesById.get(id).upload)
    this.syncPhotoInput()
  }

  syncPhotoInput() {
    const transfer = new DataTransfer()
    this.files.forEach((file, index) => {
      if (!this.removedFileIds.has(this.fileIds[index]) && !this.uploads[index]?.id) transfer.items.add(file)
    })
    this.inputTarget.files = transfer.files
    if (this.hasUploadFieldsTarget) {
      this.uploadFieldsTarget.replaceChildren()
      this.files.forEach((_file, index) => {
        if (this.removedFileIds.has(this.fileIds[index])) return
        const upload = this.uploads[index]
        if (upload?.id) this.appendUploadField("listing_photo_upload_ids[]", upload.id)
        this.appendUploadField("listing_photo_order[]", upload?.id ? `upload:${upload.id}` : "multipart")
      })
    }
  }

  appendUploadField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    this.uploadFieldsTarget.append(input)
  }

  async uploadPhoto(index) {
    if (!this.draftValue || !this.uploadValue || this.uploads[index]?.id) return

    const fileId = this.fileIds[index]
    const file = this.files[index]
    const data = new FormData()
    data.append("draft_token", this.field("submission_id")?.value || this.element.querySelector("#listing_submission_id").value)
    data.append("photo", file)
    try {
      const response = await fetch(this.uploadValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
        body: data,
        signal: AbortSignal.timeout(10_000)
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error?.message || "Fotografii se nepodařilo nahrát.")
      const currentIndex = this.fileIds.indexOf(fileId)
      if (currentIndex < 0) {
        this.deleteUpload(body)
        return
      }
      this.uploads[currentIndex] = body
      this.syncPhotoInput()
      this.syncUploadStatus(currentIndex)
      this.save()
      if (!["ready", "failed"].includes(body.state)) this.pollUpload(body.id)
    } catch (error) {
      if (!this.fileIds.includes(fileId)) return
      this.photoErrorTarget.textContent = `${error.message} Fotografii odešleme spolu s formulářem.`
    }
  }

  async validateRestoredUploads() {
    await Promise.all(this.uploads.filter(upload => upload?.id).map(upload => this.pollUpload(upload.id, true)))
  }

  async pollUpload(uploadId, restored = false) {
    const index = this.uploads.findIndex(candidate => candidate?.id === uploadId)
    const upload = this.uploads[index]
    if (!upload?.id || !this.element.isConnected) return
    try {
      const response = await fetch(`${this.uploadValue}/${upload.id}/status`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken(), "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ draft_token: this.element.querySelector("#listing_submission_id").value }),
        signal: AbortSignal.timeout(5_000)
      })
      if (!response.ok && restored) {
        await this.replaceExpiredUpload(uploadId)
        return
      }
      if (!response.ok) {
        setTimeout(() => this.pollUpload(uploadId), 2500)
        return
      }
      const currentIndex = this.uploads.findIndex(candidate => candidate?.id === uploadId)
      if (currentIndex < 0) return
      this.uploads[currentIndex] = await response.json()
      this.syncUploadStatus(currentIndex)
      this.save()
      if (!["ready", "failed"].includes(this.uploads[currentIndex].state)) setTimeout(() => this.pollUpload(uploadId), 1200)
    } catch {
      if (restored) await this.replaceExpiredUpload(uploadId)
      else setTimeout(() => this.pollUpload(uploadId), 2500)
    }
  }

  async replaceExpiredUpload(uploadId) {
    const index = this.uploads.findIndex(candidate => candidate?.id === uploadId)
    if (index < 0) return

    this.uploads[index] = null
    this.syncPhotoInput()
    this.syncUploadStatus(index)
    await this.save()
    await this.uploadPhoto(index)
  }

  syncUploadStatus(index) {
    this.photosTarget.querySelectorAll("[data-photo-upload-status]")[index]?.replaceChildren(this.uploadStatusText(this.uploads[index]))
  }

  uploadStatusText(upload) {
    if (!upload) return "Fotografii připravujeme k nahrání."
    if (upload.state === "ready") return "Fotografie je připravená."
    if (upload.state === "failed") return "Automatická kontrola fotografie se nepodařila. Inzerát po odeslání zveřejníme bez ní."
    return "Analyzujeme fotografii…"
  }

  deleteUpload(upload) {
    if (!upload?.id) return
    fetch(`${this.uploadValue}/${upload.id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this.csrfToken(), "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ draft_token: this.element.querySelector("#listing_submission_id").value })
    }).catch(() => {})
  }

  csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  photoPreviewUnavailable(image, filename) {
    const message = document.createElement("p")
    message.className = "aspect-square w-full overflow-auto rounded-sm bg-beige p-4 text-sm break-words"
    message.textContent = `${filename} – Fotka je vybraná. Tento prohlížeč neumí náhled; připravíme ho po odeslání.`
    image.replaceWith(message)
  }

  error(field, message, source = "client") {
    const text = document.createElement("p")
    text.className = "form-client-error"
    text.dataset.validationError = source
    text.dataset.validationField = field.id
    text.id = `${field.id}_error`
    text.textContent = message
    field.setAttribute("aria-invalid", "true")
    field.setAttribute("aria-describedby", [...new Set(`${field.getAttribute("aria-describedby") || ""} ${text.id}`.trim().split(/\s+/))].join(" "))
    field.after(text)
  }

  clearCorrectedValidationErrors() {
    this.element.querySelectorAll('[data-validation-error="client"]').forEach(error => {
      const field = this.element.querySelector(`#${CSS.escape(error.dataset.validationField)}`)
      if (!field?.validity.valid) return

      this.removeValidationError(error, field)
    })
  }

  removeValidationError(error, field = this.element.querySelector(`#${CSS.escape(error.dataset.validationField)}`)) {
    error.remove()
    if (!field) return

    const describedBy = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== error.id)
    if (describedBy.length) field.setAttribute("aria-describedby", describedBy.join(" "))
    else field.removeAttribute("aria-describedby")
    if (!this.element.querySelector(`[data-validation-field="${CSS.escape(field.id)}"]`)) field.removeAttribute("aria-invalid")
  }

  updateCustomValidity() {
    const price = this.field("price_amount")
    price.setCustomValidity(price.value && !/^\d+([,.]\d{1,2})?$/.test(price.value) ? "Zadejte cenu číslem, například 2500 nebo 2500,50." : "")
    const handover = this.field("personal_handover")
    handover.setCustomValidity(!handover.checked && !this.field("shipping").checked ? "Vyberte osobní předání nebo zaslání." : "")
    const contact = this.field("contact_form_enabled")
    contact.setCustomValidity(!contact.checked && !this.field("contact_email_enabled").checked && !this.field("contact_phone").value ? "Vyberte alespoň jeden kontakt." : "")
    const retainedNewPhotos = this.fileIds.some(id => !this.removedFileIds.has(id))
    this.inputTarget.setCustomValidity(!this.field("listing_type_wanted").checked && !retainedNewPhotos && !this.element.querySelector("input[name='listing[remove_photo_ids][]']:not(:checked)") ? "Přidejte alespoň jednu vlastní fotografii." : "")
  }

  showServerErrors() {
    Object.entries(this.errorsValue).forEach(([name, messages]) => {
      const field = this.field(name === "price_cents" ? "price_amount" : name)
      if (field) this.error(field, messages.join(". "), "server")
    })
    this.element.querySelector("[aria-invalid=true]")?.focus()
  }

  validate() {
    this.element.querySelectorAll("[data-validation-error]").forEach(error => this.removeValidationError(error))
    this.updateContext()
    this.updateCustomValidity()
    const invalid = Array.from(this.element.elements).filter(field => field.willValidate && !field.validity.valid)
    invalid.forEach(field => this.error(field, field.validity.valueMissing ? "Vyplňte tuto položku." : field.validationMessage))
    invalid[0]?.focus()
    return !invalid.length
  }

  preview() {
    if (!this.ready || this.processingPhotos || !this.validate()) return
    document.dispatchEvent(new CustomEvent("makerbazar:analytics", { detail: { event: "listing_form_previewed" } }))
    this.summaryTarget.replaceChildren()
    const text = (tag, value, className = "") => {
      const element = document.createElement(tag)
      element.className = className
      element.textContent = value
      this.summaryTarget.append(element)
    }
    const wanted = this.field("listing_type_wanted").checked
    text("p", wanted ? "SHÁNÍM" : "PRODÁVÁM", "text-xs font-semibold tracking-wide text-action")
    text("h3", this.field("title").value, "text-2xl font-semibold break-words")
    const amount = this.field("price_amount").value
    const number = Number(amount.replace(",", "."))
    const price = `${wanted ? "Do " : ""}${number.toLocaleString("cs-CZ")} ${this.field("currency").value === "eur" ? "€" : "Kč"}`
    text("p", amount ? (!wanted && number === 0 ? "Zdarma" : price) : "Rozpočet neuveden", "text-3xl font-semibold")
    if (!wanted && Number(this.field("quantity").value) > 1) text("p", `${this.field("quantity").value} kusů dostupných`)
    text("p", [this.field("personal_handover").checked && this.field("locality").value.trim(), this.field("country").selectedOptions[0].textContent].filter(Boolean).join(", "))
    const photos = document.createElement("div")
    photos.className = "grid grid-cols-2 gap-3 sm:grid-cols-3"
    this.urls.forEach((url, index) => {
      const image = document.createElement("img")
      image.src = url
      image.alt = `${this.field("title").value} – fotografie ${index + 1}`
      image.className = "aspect-square w-full rounded-sm object-cover"
      image.addEventListener("error", () => this.photoPreviewUnavailable(image, this.files[index].name), { once: true })
      photos.append(image)
    })
    if (this.urls.length) this.summaryTarget.append(photos)
    text("h4", "Popis", "border-t border-warm-border pt-4 text-sm font-semibold")
    text("p", this.field("description").value, "whitespace-pre-wrap break-words text-sm leading-6")
    text("h4", "Podrobnosti a předání", "border-t border-warm-border pt-4 text-sm font-semibold")
    text("p", [this.field("category_id").selectedOptions[0].textContent, this.field("condition").selectedOptions[0].textContent].join(" · "), "text-sm")
    text("p", [this.field("personal_handover").checked && "Osobní předání", this.field("shipping").checked && "Zaslání", this.field("negotiable").checked && "Cena je k jednání"].filter(Boolean).join(" · "))
    text("h4", "Kontakt pro přihlášené zájemce", "border-t border-warm-border pt-4 text-sm font-semibold")
    text("p", [this.field("contact_form_enabled").checked && "Kontaktní formulář", this.field("contact_email_enabled").checked && "Ověřený e-mail", this.field("contact_phone").value.trim()].filter(Boolean).join(" · "), "text-sm break-words")
    this.editorTarget.hidden = true
    this.previewTarget.hidden = false
    this.previewTarget.focus()
    this.previewTarget.scrollIntoView({ block: "start" })
    this.save()
  }

  edit() {
    this.previewTarget.hidden = true
    this.editorTarget.hidden = false
    this.field("title").focus()
  }

  async submit(event) {
    if (this.processingPhotos) {
      event.preventDefault()
      return
    }
    if (!this.draftValue) {
      if (!this.validate()) event.preventDefault()
      return
    }
    if (!this.previewTarget.hidden && !this.loginValue) return
    event.preventDefault()
    if (this.previewTarget.hidden) return this.preview()
    if (await this.save()) location.assign(this.loginValue)
  }

  async submitted(event) {
    if (!this.draftValue || !event.detail.success) return
    const url = event.detail.fetchResponse?.response?.url
    if (url && new URL(url).searchParams.has("listing_id")) await this.storage("delete", this.keyValue)
  }

  async discard() {
    if (!await confirmAction("Zahodit rozepsaný inzerát i fotografie z tohoto prohlížeče?")) return
    this.uploads.forEach(upload => this.deleteUpload(upload))
    if (this.db) await this.storage("delete", this.keyValue)
    location.assign(location.pathname)
  }
}
